import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import ThemeToggle from "../../../components/ThemeToggle";
import logo from "../../../assets/logo.svg";
import ENDPOINTS from "../../../api/endpoint";
import { showSuccessToast, showErrorToast } from "../../../components/Toast";
import Loader from "../../../components/Loader";

export default function Login({ theme, setTheme }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // 👇 Clear localStorage when login page mounts
  useEffect(() => {
    localStorage.clear();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const finalValue = name === "password" ? value.replace(/\s/g, "") : value;
    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(ENDPOINTS.SEND_MFA_OTP, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        showErrorToast(data.detail || "Failed to send OTP ❌");
        throw new Error(data.detail || "Failed to send OTP");
      }

      // Store email/password for MFA resend/verify
      localStorage.setItem("pending_login", JSON.stringify(formData));

      showSuccessToast("OTP sent. Check your email 📩");
      navigate("/mfa");
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen relative">
      {/* ✅ Loader overlay */}
      {loading && <Loader />}

      {/* Left side - Login form */}
      <div className="w-full md:w-1/2 flex flex-col justify-center px-8 lg:px-20 bg-white dark:bg-gray-900 transition-colors duration-300 relative">
        <div className="absolute top-4 right-4 md:hidden">
          <ThemeToggle theme={theme} setTheme={setTheme} />
        </div>

        <div className="max-w-md w-full mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Log in
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Enter your email and password to log in
          </p>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email<span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Min. 8 characters"
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-500 dark:text-gray-300"
                >
                  {showPassword ? (
                    <AiOutlineEyeInvisible size={20} />
                  ) : (
                    <AiOutlineEye size={20} />
                  )}
                </button>
              </div>
            </div>

            <div className="text-right -mt-2">
              <Link
                to="/forgot-password"
                className="text-sm text-indigo-900 dark:text-indigo-400 hover:underline font-medium"
              >
                Forgot password?
              </Link>
            </div>

            <p className="text-xs text-center text-gray-600 dark:text-gray-400 mb-4">
              By signing in, you agree to our{" "}
              <Link
                to="/termsofservice"
                className="text-indigo-900 dark:text-indigo-400 hover:underline font-medium"
              >
                Terms and Conditions
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy"
                className="text-indigo-900 dark:text-indigo-400 hover:underline font-medium"
              >
                Privacy Policy
              </Link>
            </p>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 bg-indigo-900 text-white font-medium rounded-md shadow hover:bg-indigo-800 transition disabled:opacity-50"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          {/* <p className="mt-6 text-sm text-gray-600 dark:text-gray-400">
            Not registered yet?{" "}
            <Link
              to="/signup"
              className="text-indigo-900 dark:text-white hover:underline"
            >
              Create an Account
            </Link>
          </p> */}
        </div>
      </div>

      {/* Right side - Branding */}
      <div className="hidden md:flex w-1/2 bg-indigo-900 text-white flex-col items-center justify-center relative">
        <div className="flex flex-col items-center">
          <div className="w-24 h-24 flex items-center justify-center mb-6">
            <img src={logo} alt="Logo" />
          </div>
          <h2 className="text-7xl font-bold">Secure Chat AI</h2>
        </div>

        <div className="absolute top-4 md:top-20 right-4 md:right-20">
          <ThemeToggle theme={theme} setTheme={setTheme} />
        </div>
      </div>
    </div>
  );
}
